import { createContext, useContext, useEffect, useState } from 'react';
import {
  ADD_NEW_ACCOUNT,
  ONBOARD_STATUS,
  PLATFORM,
  STORAGE_KEYS,
  SUBSCRIPTION_STATUS,
  customArrayOperations,
  endOfDayForDateObj,
  getScopedItem,
  setScopedItem
} from '../components/common/Helper';
import RegistrationApiService from '../services/RegistrationApiService';
import DebugService from '../services/DebugService';
import SubscriptionApiService from '../services/SubscriptionApiService';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [apiToken, setApiToken] = useState(getScopedItem('apiToken') || null);
  const [backendToken, setBackendToken] = useState(getScopedItem('backendToken') || null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(ADD_NEW_ACCOUNT);
  const [accountList, setAccountList] = useState([]);
  const [selectedPlatform, setSelectedPlatform] = useState('AWS');
  const [platformList, setPlatformList] = useState([]);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(null)
  const [loadingUserDetails, setLoadingUserDetails] = useState(null)
  const [userDetails, setUserDetails] = useState({
    userId: "",
    firstname: "",
    lastname: "",
    email: "",
    isActive: 0,
    userAccountStatus: ONBOARD_STATUS.NOT_LINKED,
    subscriptionStatus: null,
    subscriptionAccess: null
  });

  const loginWithApi = (token) => {
    setApiToken(token);
    setLoggedIn(true);
    setScopedItem('apiToken', token);
    setScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN, token);
  };

  const loginWithGoogle = (token) => {
    setBackendToken(token);
    setLoggedIn(true);
    setScopedItem('backendToken', token);
    setScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN, token);
  };

  const logout = () => {
    setApiToken(null);
    setBackendToken(null);
    setLoggedIn(false);
    setLoadingUserDetails(null)
    setIsSubscriptionActive(null)
    setAccountList([])

    localStorage.clear()
    sessionStorage.clear()
  };

  const refetchUserDetails = async () => {
    await fetchUserData()
  }

  const fetchUserData = async () => {
    try {
      if (!getScopedItem('userID')) {
        DebugService.warn('Unable to fetch user details due to non-availabilty of userID')
        return
      }

      const userId = getScopedItem('userID') ?? ''
      const userDetailsReq = await RegistrationApiService.userDetails(userId)
      const userPlatformReq = await RegistrationApiService.getPlatformList(userId)

      await Promise.all([
        userDetailsReq,
        userPlatformReq
      ]).then(([userDetailsRes, userSubscriptionRes]) => {
        const userDetailsData = userDetailsRes['data'] ?? {}
        const userPlatformData = userPlatformReq['data'] ?? []
        DebugService.log('AuthProvider: User data', userDetailsData)

        // Set platform list
        setPlatformList(userPlatformData)
        if (userPlatformData.length > 0) {
          const activePlatformList = userPlatformData.filter(p => p['isActive'] === 1)
          setPlatformList(activePlatformList)

          // Set default platform
          setSelectedPlatform(activePlatformList[0]['enum'])
        }

        const userUpdatedDetails = {
          ...userDetails,
          ...userDetailsData,
          userId: userDetailsData['userId'],
          firstname: userDetailsData['firstname'],
          lastname: userDetailsData['lastname'],
          email: userDetailsData['email'],
          isActive: userDetailsData['isActive'],
          userAccountStatus: userDetailsData['userAccountStatus'],
        }

        // Set onboard status
        setIsOnboarded(userDetailsData['userAccountStatus'] === ONBOARD_STATUS.LINKED)

        setAccountList(userDetailsData['userAccounts'])
        const storedPlatform = getScopedItem(STORAGE_KEYS.PLATFORM) ?? ''
        const storedAccount = getScopedItem(STORAGE_KEYS.ACCOUNT_ID) ?? ''

        if (storedPlatform && storedAccount) {
          setSelectedPlatform(storedPlatform)
          setSelectedAccount(storedAccount)
        } else if (userDetailsData['userAccounts'].length > 0) {
          const filteredAccountList = userDetailsData['userAccounts'].filter(a => a['platformEnum'] === selectedPlatform)
          if (filteredAccountList.length > 0) {
            // Select first account id
            setSelectedAccount(filteredAccountList[0]['accountId'])
            // Update the local store
            setScopedItem(STORAGE_KEYS.PLATFORM, selectedPlatform)

            if (!getScopedItem(STORAGE_KEYS.ACCOUNT_ID)) {
              setScopedItem(STORAGE_KEYS.ACCOUNT_ID, filteredAccountList[0]['accountId'])
            }
          } else {
            DebugService.warn(`Auth Provider: Filtered Account List not found`, userDetailsData, selectedPlatform)
          }
        } else {
          DebugService.warn(`AuthProvider: Empty account list found`)
        }

        // Set user details
        setUserDetails({
          ...userDetails,
          ...userUpdatedDetails
        })

        // Set local storage
        setScopedItem('userDetails', JSON.stringify(userDetailsData));
        setScopedItem('userID', JSON.stringify(userDetailsData['userId']));
        setLoadingUserDetails(false)

        window.dispatchEvent(new Event("userDetailsEvent"));

        if (userDetailsData['userAccountStatus'] === ONBOARD_STATUS.LINKED) {
          const storedRoute = getScopedItem(STORAGE_KEYS.CURRENT_ROUTE);
          if (storedRoute) {
            setScopedItem(STORAGE_KEYS.CURRENT_ROUTE,storedRoute);
          } else {
            setScopedItem(STORAGE_KEYS.CURRENT_ROUTE,CONST_ROUTE_PATH.DASHBOARD);
          }
        }
      })

    } catch (err) {
      DebugService.log('AuthProvider: Error caught while fetching user data', err)
    }
  }

  const fetchUserSubscriptionDetails = async () => {
    DebugService.log(`AuthProvider: Fetching User subscription details for userID ${getScopedItem('userID')}`,)
    await SubscriptionApiService.getPurchasedPlanList({ userId: getScopedItem('userID'), selectedAccount: selectedAccount }).then(data => {
      const userSubscriptionData = data['data'] ?? []
      if (userSubscriptionData.length > 0) {
        const { latestRecord } = customArrayOperations(userSubscriptionData, 'createdOn')

        // Handle latest subscription status
        if (latestRecord !== undefined || latestRecord !== null) {
          // if ([SUBSCRIPTION_STATUS.AUTHENTICATED, SUBSCRIPTION_STATUS.ACTIVE].includes(latestRecord['status'])) {
          if ([SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(latestRecord['status']) && endOfDayForDateObj(new Date(latestRecord['currentEnd'])) >= (new Date())) {
            setIsSubscriptionActive(true)
            setScopedItem(STORAGE_KEYS.SUBSCRIPTION_STATUS, latestRecord['status'])
            setUserDetails(prev => ({
              ...prev,
              subscriptionStatus: latestRecord['status'],
              subscriptionAccess: latestRecord['access'],
              userSubscriptionId: latestRecord['userSubscriptionId']
            }))
          } else {
            DebugService.log(`AuthProvider: User subscription data is not active`, latestRecord)
            setIsSubscriptionActive(false)
          }
        } else {
          DebugService.log(`AuthProvider: Error caught while handling subscription based on latest purchased record`, latestRecord)
          setIsSubscriptionActive(false)
        }
      } else {
        DebugService.log(`AuthProvider: User subscription data is empty`, userSubscriptionData)
        setIsSubscriptionActive(false)
      }
    })
  }

  useEffect(() => {
    if (getScopedItem('userID')) {
      fetchUserSubscriptionDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount])

  useEffect(() => {
    // Fetch user details once on mount (handles page refresh with existing session)
    if (getScopedItem('email')) {
      setLoadingUserDetails(true)
      fetchUserData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // Handles first login: loggedIn just became true but the mount effect already ran
    // without email in localStorage, so fetchUserData() was never called.
    // loadingUserDetails === null means user data has not started loading yet.
    if (loggedIn && getScopedItem('email') && loadingUserDetails === null) {
      setLoadingUserDetails(true)
      fetchUserData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  useEffect(() => {
    const loginToken = getScopedItem(STORAGE_KEYS.LOCAL_LOGIN_TOKEN) ?? ''
    setLoggedIn(loginToken !== '')
  }, [])

  return (
    <AuthContext.Provider value={{
      apiToken,
      backendToken,
      loggedIn,
      loginWithApi,
      loginWithGoogle,
      logout,
      isOnboarded,
      setIsOnboarded,
      userDetails,
      setUserDetails,
      refetchUserDetails,
      loadingUserDetails,
      isSubscriptionActive,
      accountList,
      selectedAccount,
      setSelectedAccount,
      platformList,
      selectedPlatform,
      setSelectedPlatform
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
